import React, { useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { formatTypingNames } from '@/utils/formatTypingNames';
import {
	setActiveTeamConversation,
	markConversationAsRead,
} from '../../../actions/teams';
import ConversationMenu from './ConversationMenu';
import TeamConversationAvatar from './TeamConversationAvatar';
import { withoutCustomerToken } from './customerLink';
import { cn } from '@/lib/utils';
import { LogOut, Star, StarOff, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { ActionSheet, LONG_PRESS_CLASS, SwipeableRow, useLongPress } from '@/components/mobile';
import useTeamConversationActions from './useTeamConversationActions';

// Today shows the time, this week the day, older messages the date
const formatListTime = (date) => {
	if (!date) {
		return '';
	}

	const time = moment(date);

	if (time.isSame(moment(), 'day')) {
		return time.format('LT');
	}

	if (time.isAfter(moment().subtract(6, 'days'), 'day')) {
		return time.format('ddd');
	}

	return time.format('L');
};

// One conversation in the Teams list, laid out like the inbox's conversation rows
const ConversationSelector = ({
	assignedTypeSelected,
	conversation,
	latestMessage,
	name,
	_activeTeamConversation,
	_activeUser,
	userFromDirectMessage,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const [actionsOpen, setActionsOpen] = useState(false);

	const auth = useSelector((state) => state.auth);
	const typingIndicator = useSelector((state) => state.typingIndicator);

	const isMuted = conversation?._mutedUsers.includes(auth.user._id);
	const isStarred = conversation?._starredUsers?.includes(auth.user._id);
	const isUnread = conversation._unreadUsers.includes(_activeUser);
	const isActive = _activeTeamConversation === conversation._id;

	const directMessage = assignedTypeSelected === 'DIRECT_MESSAGES';
	const actions = useTeamConversationActions(conversation, {
		isDirectMessage: directMessage,
	});
	const longPress = useLongPress(() => setActionsOpen(true), { disabled: !isMobile });

	const title = directMessage ? name : conversation.title;

	const typingNames = typingIndicator
		.filter((t) => t._teamConversation === conversation._id)
		.map((t) => t.whoIsTyping);

	const isEvent = latestMessage?.type === 'event';
	const sender =
		!latestMessage || isEvent
			? ''
			: latestMessage._user === _activeUser
				? 'You: '
				: directMessage
					? ''
					: `${name}: `;
	const preview = latestMessage
		? latestMessage.isDeleted
			? 'Message deleted'
			: withoutCustomerToken(latestMessage.text, latestMessage._attachedCustomer) ||
				(latestMessage.attachments?.length
					? latestMessage.attachments.every((a) => a.type?.startsWith('image/'))
						? 'Sent a photo'
						: 'Sent an attachment'
					: latestMessage._attachedCustomer?.name
						? `Linked ${latestMessage._attachedCustomer.name}`
						: '')
		: 'No messages yet';

	const openConversation = () => {
		dispatch(setActiveTeamConversation(conversation._id));
		navigate(
			`/groupchat/${directMessage ? 'dms' : 'groups'}/conversation/groups/${conversation._id}?_location=${conversation._business}&_teamMessage=${latestMessage?._id || ''}&isDirectMessage=${directMessage}`
		);
		if (isUnread) {
			dispatch(markConversationAsRead(conversation, _activeUser));
		}
	};

	const rowContent = (
		<>
			<TeamConversationAvatar
				conversation={conversation}
				directMessageUser={directMessage ? userFromDirectMessage : null}
				size={isMobile ? 48 : 40}
				className={cn(isMuted && 'opacity-60')}
			/>
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<div className="flex items-start gap-2">
					<div className="flex min-w-0 flex-1 items-center gap-1.5">
						<p
							className={cn(
								'truncate text-[15px] leading-5',
								isUnread ? 'font-medium' : 'font-normal',
								isMuted && 'text-muted-foreground',
								isMobile && 'text-base',
								isMobile && isUnread && 'font-semibold'
							)}
						>
							{title || 'Untitled'}
						</p>
						{isMuted && (
							<VolumeX
								className="size-3.5 shrink-0 text-muted-foreground"
								aria-label="Muted"
							/>
						)}
					</div>
					<div className="flex shrink-0 items-center gap-1.5">
						<span className="whitespace-nowrap text-xs text-muted-foreground">
							{formatListTime(latestMessage?.createdAt || conversation.latestMessageDate)}
						</span>
						{isUnread && (
							<span
								className={cn('size-2 rounded-full bg-primary', isMobile && 'size-2.5 bg-blue-500')}
								aria-label={isMobile ? undefined : 'Unread'}
								aria-hidden={isMobile || undefined}
							/>
						)}
					</div>
				</div>
				<div className="flex items-start gap-2">
					<div className="relative min-w-0 flex-1">
						<p
							className={cn(
								'break-words text-sm leading-5 line-clamp-2',
								isUnread ? 'text-foreground/80' : 'text-muted-foreground',
								typingNames.length > 0 && 'invisible'
							)}
						>
							{sender}
							{preview}
						</p>
						{typingNames.length > 0 && (
							<p className="absolute inset-x-0 top-0 truncate text-sm leading-5 text-primary">
								{formatTypingNames(typingNames)}…
							</p>
						)}
					</div>
					{/* stops the row opening when the menu is used. Phones use the
					    long-press sheet, swipe actions and the Info page instead. */}
					{!isMobile && (
						<div
							className="shrink-0 opacity-0 transition-opacity group-hover/conversation:opacity-100 focus-within:opacity-100 touch:opacity-100"
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => e.stopPropagation()}
						>
							<ConversationMenu
								conversation={conversation}
								isDirectMessage={directMessage}
								doesCurrentUserHaveConversationMuted={isMuted}
								doesCurrentUserHaveConversationStarred={isStarred}
							/>
						</div>
					)}
				</div>
			</div>
		</>
	);

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			openConversation();
		}
	};

	if (isMobile) {
		return (
			<>
				<SwipeableRow
					leadingActions={[
						{
							label: actions.isStarred ? 'Unstar' : 'Star',
							icon: actions.isStarred ? StarOff : Star,
							className: 'bg-amber-500 text-white',
							onAction: actions.toggleStar,
						},
					]}
					trailingActions={[
						{
							label: actions.isMuted ? 'Unmute' : 'Mute',
							icon: actions.isMuted ? Volume2 : VolumeX,
							className: 'bg-indigo-500 text-white',
							onAction: actions.toggleMute,
						},
					]}
				>
					<div
						role="button"
						tabIndex={0}
						data-onboarding="teams-conversation"
						onClick={openConversation}
						onKeyDown={handleKeyDown}
						{...longPress}
						className={cn(
							'relative flex cursor-pointer items-start gap-3 border-b border-border/50 bg-background py-3 pl-4 pr-4 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring active:bg-muted/70 dark:active:bg-zinc-800',
							LONG_PRESS_CLASS
						)}
					>
						{/* read first, before the name, preview and time */}
						{isUnread && <span className="sr-only">Unread, </span>}
						{rowContent}
					</div>
				</SwipeableRow>
				<ActionSheet
					open={actionsOpen}
					onOpenChange={setActionsOpen}
					title={title || 'Untitled'}
					items={[
						{
							label: actions.isStarred ? 'Unstar' : 'Star',
							icon: actions.isStarred ? StarOff : Star,
							onSelect: actions.toggleStar,
						},
						{
							label: actions.isMuted ? 'Unmute' : 'Mute',
							icon: actions.isMuted ? Volume2 : VolumeX,
							onSelect: actions.toggleMute,
						},
						...(actions.canLeave
							? [
									{
										label: 'Leave group',
										icon: LogOut,
										destructive: true,
										onSelect: actions.leave,
									},
								]
							: []),
					]}
				/>
			</>
		);
	}

	return (
		<div
			role="button"
			tabIndex={0}
			key={conversation._id}
			data-onboarding="teams-conversation"
			onClick={openConversation}
			onKeyDown={handleKeyDown}
			className={cn(
				'group/conversation flex cursor-pointer items-start gap-3 border-b border-border/50 px-4 py-3.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
				isActive
					? 'bg-stone-100 dark:bg-stone-800/70'
					: 'hover:bg-stone-50 dark:hover:bg-stone-900/60'
			)}
		>
			{rowContent}
		</div>
	);
};

export default ConversationSelector;
